// The admin console's API usage tab: every Songstats request we sent this month, counted against the artist or
// track it asked about. Songstats limits each of those resources to a monthly number of requests on its own
// allowance, which no per-band budget can show — this is where a resource close to its limit, or already
// answered 429, becomes visible before a band's numbers go stale.
import { useMemo, useState } from "react";
import { Card, DataTable, EmptyState, Field, Picker, Tag, type DataTableColumn } from "@cntrl/ui";
import { errorMessage } from "../../app/hooks/apiError.js";
import { useAdminVendorUsage, type AdminVendorResourceRow } from "../../app/hooks/useAdmin.js";
import { spotifyArtistUrl, spotifyTrackUrl } from "../../app/vendorLinks.js";
import { ago, dash, exact, matches } from "./adminFormat.js";
import styles from "./AdminDashboard.module.scss";

/** "2026-10" → "October 2026". The month is a UTC calendar month, so it is formatted as one. */
function monthLabel(month: string): string {
  const d = new Date(`${month}-01T00:00:00Z`);
  return Number.isNaN(d.getTime())
    ? month
    : d.toLocaleDateString(undefined, { month: "long", year: "numeric", timeZone: "UTC" });
}

/** Spotify ids open their Spotify page; any other id is shown as text. */
function ResourceCell({ row }: { row: AdminVendorResourceRow }) {
  const kind = row.kind === "track" ? "track" : "artist";
  const href = row.idParam?.startsWith("spotify_") && row.resourceId
    ? (kind === "track" ? spotifyTrackUrl(row.resourceId) : spotifyArtistUrl(row.resourceId))
    : undefined;
  return (
    <span className={styles.inline}>
      <Tag tone="neutral" size="sm">{kind}</Tag>
      {href ? (
        <a className={styles.code} href={href} target="_blank" rel="noreferrer">{row.resourceId}</a>
      ) : (
        <code className={styles.code}>{row.resourceId}</code>
      )}
    </span>
  );
}

/** A count that only matters when it is not zero. */
function IssueCount({ n, tone }: { n: number | undefined; tone: "danger" | "warning" }) {
  if (!n) return <span className={styles.zero}>0</span>;
  return <Tag tone={tone} size="sm">{n.toLocaleString()}</Tag>;
}

const columns = (limit: number, current: boolean): DataTableColumn<AdminVendorResourceRow>[] => [
  { key: "resource", header: "Resource", cell: (r) => <ResourceCell row={r} /> },
  { key: "band", header: "Band", cell: (r) => r.bandName ?? <span className={styles.zero}>{r.bandId ? dash : "platform"}</span> },
  {
    key: "calls",
    header: "Requests",
    align: "end",
    cell: (r) => (
      <span className={styles.audience}>
        <strong>{exact(r.calls)}</strong>
        <span className={styles.sub}>{Math.round(((r.calls ?? 0) / limit) * 100)}% of {limit.toLocaleString()}</span>
      </span>
    ),
  },
  ...(current
    ? [{
        key: "projected",
        header: "Month-end pace",
        align: "end" as const,
        cell: (r: AdminVendorResourceRow) =>
          (r.projectedCalls ?? 0) > limit ? (
            <Tag tone="danger" size="sm">{exact(r.projectedCalls)}</Tag>
          ) : (
            exact(r.projectedCalls)
          ),
      }]
    : []),
  { key: "429", header: "429s", align: "end", cell: (r) => <IssueCount n={r.rateLimited} tone="danger" /> },
  { key: "refused", header: "Refused", align: "end", cell: (r) => <IssueCount n={r.refused} tone="warning" /> },
  { key: "failed", header: "No answer", align: "end", cell: (r) => <IssueCount n={r.failed} tone="warning" /> },
  { key: "status", header: "Last status", align: "end", cell: (r) => (r.lastStatus ? String(r.lastStatus) : dash) },
  { key: "last", header: "Last request", cell: (r) => ago(r.lastCalledOn) },
  { key: "path", header: "Endpoint", cell: (r) => <code className={styles.code}>{r.lastPath}</code> },
];

/** Songstats requests per artist and track for one month, busiest first. */
export function VendorUsageTab() {
  const [month, setMonth] = useState<string | undefined>(undefined);
  const [query, setQuery] = useState("");
  const q = useAdminVendorUsage(month);
  const panel = q.data;

  const rows = useMemo(
    () => (panel?.rows ?? []).filter((r) => matches(query, r.resourceId, r.bandName, r.lastPath)),
    [panel?.rows, query],
  );

  if (q.isLoading) return <Card title="Songstats API usage" subtitle="Reading this month's requests…" />;
  if (q.isError || !panel) {
    return (
      <Card title="Songstats API usage">
        <p className={styles.error}>{errorMessage(q.error, "Could not read API usage.")}</p>
      </Card>
    );
  }

  const limit = panel.monthlyLimit ?? 1000;
  const current = panel.isCurrentMonth === true;
  const months = (panel.months ?? []).map((m) => ({ value: m, label: monthLabel(m) }));
  const shown = rows.length;
  const total = panel.resourceTotal ?? 0;

  return (
    <Card
      title="Songstats API usage"
      subtitle={`Every request we sent Songstats, counted against the artist or track it asked about. Songstats allows ${limit.toLocaleString()} requests per artist and per track each calendar month (UTC); a 429 means that resource has used its month.`}
    >
      <div className={styles.stack}>
        <dl className={styles.headline}>
          <div>
            <dt>Requests</dt>
            <dd>{exact(panel.totalCalls)}</dd>
          </div>
          <div>
            <dt>Resources</dt>
            <dd>{exact(total)}</dd>
          </div>
          <div>
            <dt>Artists · tracks</dt>
            <dd>{`${exact(panel.artistResources)} · ${exact(panel.trackResources)}`}</dd>
          </div>
          {current && (
            <div>
              <dt>On pace to pass the limit</dt>
              <dd data-alert={(panel.overPace ?? 0) > 0 || undefined}>{exact(panel.overPace)}</dd>
            </div>
          )}
          <div>
            <dt>Answered 429</dt>
            <dd data-alert={(panel.rateLimitedResources ?? 0) > 0 || undefined}>{exact(panel.rateLimitedResources)}</dd>
          </div>
        </dl>

        <div className={styles.controls}>
          <div className={styles.search}>
            <Field label="Search" hideLabel type="search" placeholder="Search id, band or endpoint…" value={query}
              onChange={(e) => setQuery(e.target.value)} />
          </div>
          <Picker label="Month" hideLabel value={panel.month ?? ""} options={months}
            onChange={(v) => setMonth(v || undefined)} />
          <span className={styles.count}>
            {total > (panel.rows?.length ?? 0) ? `busiest ${panel.rows?.length} of ${total}` : `${shown} of ${total}`}
          </span>
        </div>

        {total === 0 ? (
          <EmptyState title="No requests" description={`Nothing was sent to Songstats in ${monthLabel(panel.month ?? "")}.`} />
        ) : shown === 0 ? (
          <EmptyState title="Nothing matches" description="Try a different search." />
        ) : (
          <DataTable columns={columns(limit, current)} rows={rows}
            rowKey={(r) => `${r.kind}/${r.resourceId}`} maxBlockSize="40rem" />
        )}
      </div>
    </Card>
  );
}
